import { AxisBottom } from "@/components/AxisBottom";
import { AxisLeft } from "@/components/AxisLeft";
import { getCompRegressionPath, pathAnimationProps } from "@/lib/chartUtils";
import { ChartStuff } from "@/lib/deriveChartStuff";
import { formatDate, formatPrice } from "@/lib/formatters";
import { NormalizedDataPoint } from "@/lib/PreparedDataset";
import RegressionModel from "@/types/RegressionModel";
import * as d3 from "d3";
import { motion } from "framer-motion";

const TOOLTIP_WIDTH = 200;
const MARGIN = { top: 10, right: 10 + TOOLTIP_WIDTH, bottom: 30, left: 90 };

type ChartElementsProps = {
  width: number;
  height: number;
  data: NormalizedDataPoint[];
  subject?: { date: Date; name: string };
  setHoveredData?: (val: string | undefined) => void;
  regressionType: RegressionModel | null;
  onPointClick: (point: NormalizedDataPoint) => void;
  chartStuff: ChartStuff | null;
  setTooltipData: (data: any) => void;
  xScale: d3.ScaleLinear<number, number>;
  yScale: d3.ScaleLinear<number, number>;
};

export function ChartElements({
  width,
  height,
  data,
  subject,
  setHoveredData,
  regressionType,
  onPointClick,
  chartStuff,
  setTooltipData,
  xScale,
  yScale,
}: ChartElementsProps) {
  const boundsWidth = width - MARGIN.right - MARGIN.left;
  const boundsHeight = height - MARGIN.top - MARGIN.bottom;

  if (!chartStuff) return null;
  const { regressionData, adjustedCompData } = chartStuff;

  const regressionPath =
    regressionData &&
    d3
      .line()
      .x((d) => xScale(d[0]))
      .y((d) => yScale(d[1]))(regressionData);

  return (
    <>
      {/* vertical axis */}
      <AxisLeft yScale={yScale} pixelsPerTick={40} width={boundsWidth} />

      {/* horizontal axis */}
      <g transform={`translate(0, ${boundsHeight})`}>
        <AxisBottom xScale={xScale} width={boundsWidth} height={boundsHeight} />
      </g>

      {/* trend line */}
      <motion.path
        className={"stroke-primary"}
        key={regressionType}
        {...pathAnimationProps}
        stroke="current"
        strokeWidth={2}
        strokeOpacity={0.6}
        fill="transparent"
        d={regressionPath || undefined}
      />

      {/* comp on trend line */}
      {regressionData &&
        adjustedCompData?.map((comp, i) => {
          if (!comp.adjustedData) return null;
          return (
            <path
              key={i}
              d={
                getCompRegressionPath(comp.adjustedData, xScale, yScale, regressionData) ||
                undefined
              }
              stroke="current"
              className="stroke-muted-foreground"
              strokeWidth={1}
              strokeOpacity={0.5}
              strokeDasharray="4 4"
              fill="transparent"
            />
          );
        })}

      {/* adjusted comp points */}
      {subject?.date &&
        regressionData &&
        adjustedCompData?.map((adjustedComp) => {
          const comp = adjustedComp;
          if (!comp.adjustedData?.adjustedValue) return null;

          const d = data.find((d) => d.id === comp.compId);
          if (!d) return null;

          return (
            <g key={comp.compId}>
              <circle
                r={2}
                cx={xScale(comp.subjectSaleDate)}
                cy={yScale(comp.adjustedData.adjustedValue)}
                fill="current"
                className={"fill-foreground cursor-pointer"}
                onClick={() => onPointClick(d)}
              />
            </g>
          );
        })}

      {/* subject sale date line */}
      {subject?.date && (
        <line
          x1={xScale(subject.date.getTime())}
          y1={0}
          x2={xScale(subject.date.getTime())}
          y2={boundsHeight}
          stroke="current"
          className="stroke-muted-foreground"
          strokeWidth={1}
          strokeOpacity={0.5}
          strokeDasharray="4 4"
        />
      )}

      {/* scatterplot points - putting toward the end, because onMouseOver needs it to be on top. */}
      {data.map((d, i) => {
        const className = !d.comp ? "cursor-pointer fill-muted-foreground/50" : "cursor-pointer";

        return (
          <circle
            key={i}
            r={4}
            cx={xScale(d.date.getTime())}
            cy={yScale(d.price)}
            fill="current"
            className={className}
            fillOpacity={0.7}
            onClick={() => onPointClick(d)}
            onMouseOver={() => {
              if (setHoveredData) {
                setHoveredData(d.id);
              }
              setTooltipData({
                xPos: xScale(d.date.getTime()),
                yPos: yScale(d.price),
                component: (
                  <div className="text-sm">
                    <div className="font-bold">{d.address}</div>
                    <div className="text-muted-foreground">{formatDate(d.date.getTime())}</div>
                    <div className="text-muted-foreground">{formatPrice(d.price)}</div>
                  </div>
                ),
              });
            }}
            onMouseLeave={() => {
              if (setHoveredData) {
                setHoveredData(undefined);
              }
              setTooltipData(null);
            }}
          />
        );
      })}
    </>
  );
}
